import React, { useMemo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import './UnifiedTable.css';
import TableToolbar from './TableToolbar';
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import TableFooter from './TableFooter';
import TableLightbox from './TableLightbox';
import useTableState from './hooks/useTableState';
import useColumnResize from './hooks/useColumnResize';
import useColumnReorder from './hooks/useColumnReorder';
import useColumnVisibility from './hooks/useColumnVisibility';
import { DEFAULT_COLUMN } from './utils/constants';
import { sanitizeForCopy } from './utils/formatters';

function UnifiedTable({
    tableId,
    columns: rawColumns,
    data = [],
    totalRows,
    page: controlledPage,
    pageSize: controlledPageSize,
    pageSizes,
    showPagination = true,
    onPageChange,
    onPageSizeChange,
    sortKey: controlledSortKey,
    sortDir: controlledSortDir,
    onSortChange,
    serverSort = false,
    search,
    onSearchChange,
    columnFilters,
    onColumnFilterChange,
    selectable = false,
    selectedRows: controlledSelected,
    onSelectionChange,
    toolbarActions,
    bulkActions,
    exportFilename,
    exportFormat = 'csv',
    onExportCsv = true,
    onCopyTable = true,
    onImageClick: externalImageClick,
    onTextOverflow: externalTextOverflow,
    conditionalFormats,
    fullscreenEnabled = false,
    compareEnabled = false,
    printEnabled = false,
    rowDragEnabled = false,
    virtualScrollEnabled = false,
    onRowClick,
    onRowOrderChange,
    getRowKey,
    loading = false,
    onColumnWidthChange,
    onColumnOrderChange,
    onHiddenColumnsChange,
    hiddenColumns: controlledHiddenColumns,
}) {
    const containerRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // --- Built-in lightbox state ---
    const [lightbox, setLightbox] = useState({
        isOpen: false,
        mode: 'image',
        imageUrl: '',
        text: '',
    });

    const handleImageClick = useCallback(
        (url) => {
            if (externalImageClick) {
                externalImageClick(url);
            } else {
                setLightbox({ isOpen: true, mode: 'image', imageUrl: url, text: '' });
            }
        },
        [externalImageClick]
    );

    const handleTextOverflow = useCallback(
        (text) => {
            if (externalTextOverflow) {
                externalTextOverflow(text);
            } else {
                setLightbox({ isOpen: true, mode: 'text', imageUrl: '', text });
            }
        },
        [externalTextOverflow]
    );

    const closeLightbox = useCallback(() => {
        setLightbox({ isOpen: false, mode: 'image', imageUrl: '', text: '' });
    }, []);

    // --- Merge columns with defaults ---
    const columns = useMemo(
        () => rawColumns.map((col) => ({ ...DEFAULT_COLUMN, ...col })),
        [rawColumns]
    );

    // Columns eligible for user hide/show (statically hidden columns are excluded
    // from the "manage columns" menu — they are never shown regardless).
    const manageableColumns = useMemo(
        () => columns.filter((col) => !col.hidden),
        [columns]
    );

    // --- Column visibility (hide/show) hook ---
    const { hiddenColumns, hiddenSet, toggleColumn, showAll: showAllColumns } =
        useColumnVisibility({
            tableId,
            columns: manageableColumns,
            onHiddenColumnsChange,
            controlledHidden: controlledHiddenColumns,
        });

    const visibleColumns = useMemo(
        () => manageableColumns.filter((col) => !hiddenSet.has(col.key)),
        [manageableColumns, hiddenSet]
    );

    // --- Column resize & reorder hooks ---
    const { columnWidths, isResizing, getResizeHandleProps, resetColumnWidth } =
        useColumnResize({ onColumnWidthChange });

    const { reorderedColumns, dragState, getDragHandleProps, resetColumnOrder } =
        useColumnReorder({ visibleColumns, onColumnOrderChange });

    // --- Effective columns: reordered + width overrides ---
    const effectiveColumns = useMemo(() => {
        return reorderedColumns.map((col) => {
            const w = columnWidths[col.key];
            // minWidth колонки тоже опускаем до заданной ширины, иначе
            // исходный minWidth не даёт сжать столбец перетаскиванием.
            if (w != null) {
                return { ...col, width: w, minWidth: w, resizeMinWidth: col.minWidth };
            }
            return col;
        });
    }, [reorderedColumns, columnWidths]);

    // --- Natural column widths ---
    // The table is `width: max-content`, so as long as any column is left without
    // an explicit width the browser sizes it from cell content — and a single long
    // row then pins the column, making a drag-to-shrink stop halfway. Measuring the
    // rendered header once and pinning every column through a <colgroup> makes the
    // layout fully definite, so a resize is honoured exactly.
    const tableRef = useRef(null);
    const [naturalWidths, setNaturalWidths] = useState({});
    const columnsSignature = useMemo(
        () => effectiveColumns.map((col) => col.key).join('|'),
        [effectiveColumns]
    );
    const measuredSignatureRef = useRef(null);

    // --- Table state hook ---
    const state = useTableState({
        columns,
        data,
        totalRows,
        page: controlledPage,
        pageSize: controlledPageSize,
        onPageChange,
        onPageSizeChange,
        sortKey: controlledSortKey,
        sortDir: controlledSortDir,
        onSortChange,
        serverSort,
        search,
        onSearchChange,
        selectedRows: controlledSelected,
        onSelectionChange,
    });

    // Measure once the first page of rows is on screen, so the pinned widths match
    // what the content-driven layout produced rather than a header-only guess.
    useLayoutEffect(() => {
        if (measuredSignatureRef.current === columnsSignature) return;
        if (!state.displayData.length) return;
        const table = tableRef.current;
        if (!table) return;
        const cells = table.querySelectorAll('thead tr > th');
        // +1 for the leading row-actions column, which has a fixed width in CSS.
        if (cells.length !== effectiveColumns.length + 1) return;
        const measured = {};
        effectiveColumns.forEach((col, idx) => {
            const width = Math.round(cells[idx + 1].getBoundingClientRect().width);
            if (width > 0) measured[col.key] = width;
        });
        if (Object.keys(measured).length !== effectiveColumns.length) return;
        measuredSignatureRef.current = columnsSignature;
        setNaturalWidths(measured);
    }, [columnsSignature, effectiveColumns, state.displayData]);

    // --- Sticky column positioning (from effectiveColumns) ---
    const stickyOffsets = useMemo(() => {
        const offsets = {};
        let left = 36; // row-actions column width
        for (let i = 0; i < effectiveColumns.length; i++) {
            const col = effectiveColumns[i];
            if (col.sticky) {
                offsets[col.key] = left;
                left += col.width || naturalWidths[col.key] || 150;
            }
        }
        return offsets;
    }, [effectiveColumns, naturalWidths]);

    // --- Export data builder ---
    // Выгружаем все строки после поиска/сортировки, а не только текущую страницу:
    // иначе из таблицы на тысячи строк в файл попадали первые 50.
    // raw=true оставляет числа числами (для Excel).
    const getExportData = useCallback((raw = false) => {
        const exportColumns = effectiveColumns.filter((col) => col.exportable !== false);
        const headers = exportColumns.map((col) => col.header);
        const rows = state.allData.map((row) =>
            exportColumns.map((col) => {
                const realIdx = columns.indexOf(col);
                const val = Array.isArray(row) ? row[realIdx] : row[col.key];
                if (raw && typeof val === 'number' && Number.isFinite(val)) return val;
                return sanitizeForCopy(val);
            })
        );
        return { headers, rows };
    }, [effectiveColumns, state.allData, columns]);

    // --- Row keys for selection ---
    const allRowKeys = useMemo(() => {
        return state.displayData.map((row, index) => {
            if (getRowKey) return getRowKey(row, index);
            if (Array.isArray(row)) return `row-${state.sliceStart + index}`;
            return row.id || `row-${state.sliceStart + index}`;
        });
    }, [state.displayData, state.sliceStart, getRowKey]);

    const allSelected = state.selected.length > 0 && state.selected.length === allRowKeys.length;

    const handleToggleAllSelect = useCallback(() => {
        state.toggleAllSelection(allRowKeys);
    }, [state, allRowKeys]);

    // --- Fullscreen ---
    const toggleFullscreen = useCallback(() => {
        if (!containerRef.current) return;
        if (!isFullscreen) {
            if (containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen();
            }
            setIsFullscreen(true);
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
            setIsFullscreen(false);
        }
    }, [isFullscreen]);

    React.useEffect(() => {
        const handler = () => {
            if (!document.fullscreenElement) setIsFullscreen(false);
        };
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    const containerClass = [
        'ut-container',
        isFullscreen ? 'ut-container--fullscreen' : '',
        isResizing ? 'ut-container--resizing' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={containerClass} ref={containerRef} data-table-id={tableId}>
            <TableToolbar
                page={state.page}
                totalPages={state.totalPages}
                pageSize={state.pageSize}
                pageSizes={pageSizes}
                showPagination={showPagination}
                onPageChange={state.handlePageChange}
                onPageSizeChange={state.handlePageSizeChange}
                search={state.search}
                onSearchChange={onSearchChange ? state.handleSearchChange : undefined}
                toolbarActions={toolbarActions}
                exportFilename={exportFilename}
                exportFormat={exportFormat}
                showExportCsv={onExportCsv}
                showCopyTable={onCopyTable}
                getExportData={getExportData}
                selectable={selectable}
                selectedCount={state.selected.length}
                bulkActions={bulkActions}
                fullscreenEnabled={fullscreenEnabled}
                isFullscreen={isFullscreen}
                onToggleFullscreen={toggleFullscreen}
                loading={loading}
                manageableColumns={manageableColumns}
                hiddenColumns={hiddenColumns}
                onToggleColumn={toggleColumn}
                onShowAllColumns={showAllColumns}
            />

            <div className="ut-scroll">
                <table className="ut-table" ref={tableRef}>
                    <colgroup>
                        <col style={{ width: 36 }} />
                        {effectiveColumns.map((col) => {
                            const width = col.width ?? naturalWidths[col.key];
                            return (
                                <col
                                    key={col.key}
                                    style={width != null ? { width } : undefined}
                                />
                            );
                        })}
                    </colgroup>

                    <TableHeader
                        visibleColumns={effectiveColumns}
                        sortKey={state.sortKey}
                        sortDir={state.sortDir}
                        onSortChange={state.handleSortChange}
                        selectable={selectable}
                        allSelected={allSelected}
                        onToggleAllSelect={handleToggleAllSelect}
                        stickyOffsets={stickyOffsets}
                        getResizeHandleProps={getResizeHandleProps}
                        resetColumnWidth={resetColumnWidth}
                        getDragHandleProps={getDragHandleProps}
                        dragState={dragState}
                        isResizing={isResizing}
                    />

                    <TableBody
                        data={state.displayData}
                        columns={columns}
                        visibleColumns={effectiveColumns}
                        sliceStart={state.sliceStart}
                        selectedRows={state.selected}
                        selectable={selectable}
                        onToggleSelect={state.toggleRowSelection}
                        onRowClick={onRowClick}
                        onImageClick={handleImageClick}
                        onTextOverflow={handleTextOverflow}
                        conditionalFormats={conditionalFormats}
                        stickyOffsets={stickyOffsets}
                        getRowKey={getRowKey}
                        loading={loading}
                    />

                    <TableFooter
                        visibleColumns={effectiveColumns}
                        data={state.displayData}
                        columns={columns}
                        stickyOffsets={stickyOffsets}
                    />
                </table>
            </div>

            <TableLightbox
                isOpen={lightbox.isOpen}
                mode={lightbox.mode}
                imageUrl={lightbox.imageUrl}
                text={lightbox.text}
                onClose={closeLightbox}
            />
        </div>
    );
}

export default UnifiedTable;
